import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useListTasks, useCreateTask, useUpdateTask, useDeleteTask, getListTasksQueryKey } from "@workspace/api-client-react";
import {
  Trash2, CheckCircle2, Circle, CalendarDays, ArrowUpDown,
  Flag, X, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, isToday, isPast, parseISO } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

type Priority = "high" | "medium" | "low";
type SortKey = "priority" | "dueDate" | "createdAt";

const PRIORITY_ORDER: Record<Priority, number> = { high: 0, medium: 1, low: 2 };

const PRIORITY_CONFIG: Record<Priority, { label: string; dot: string; badge: string }> = {
  high:   { label: "High",   dot: "bg-red-500",    badge: "bg-red-100 text-red-700" },
  medium: { label: "Medium", dot: "bg-amber-400",  badge: "bg-amber-100 text-amber-700" },
  low:    { label: "Low",    dot: "bg-green-500",  badge: "bg-green-100 text-green-700" },
};

function PriorityPicker({ value, onChange }: { value: Priority; onChange: (p: Priority) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors ${PRIORITY_CONFIG[value].badge} hover:opacity-80`}
        >
          <Flag className="h-3 w-3" />
          {PRIORITY_CONFIG[value].label}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-36 p-1" align="start">
        {(["high", "medium", "low"] as Priority[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors hover:bg-gray-100 ${value === p ? "font-semibold" : ""}`}
          >
            <span className={`w-2 h-2 rounded-full ${PRIORITY_CONFIG[p].dot}`} />
            {PRIORITY_CONFIG[p].label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function DueDateButton({
  value,
  onChange,
  onClear,
}: {
  value: string | null | undefined;
  onChange: (d: string | null) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);

  const dateObj = value ? parseISO(value) : undefined;
  const overdue = dateObj && isPast(dateObj) && !isToday(dateObj);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 transition-colors ${
            value
              ? overdue
                ? "text-red-600 bg-red-50 hover:bg-red-100"
                : "text-blue-600 bg-blue-50 hover:bg-blue-100"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          }`}
        >
          <CalendarDays className="h-3 w-3" />
          {value ? format(parseISO(value), "d MMM") : "Due date"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={dateObj}
          onSelect={(d) => {
            onChange(d ? format(d, "yyyy-MM-dd") : null);
            setOpen(false);
          }}
          initialFocus
        />
        {value && (
          <div className="border-t p-2">
            <Button variant="ghost" size="sm" className="w-full text-xs text-red-500 hover:text-red-700" onClick={() => { onClear(); setOpen(false); }}>
              Remove due date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function Tasks() {
  const { data: tasks, isLoading } = useListTasks();
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState<Priority>("medium");
  const [newDueDate, setNewDueDate] = useState<string | null>(null);
  const [newDueDateOpen, setNewDueDateOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("priority");
  const [showCompleted, setShowCompleted] = useState(false);

  const invalidateTasks = () => queryClient.invalidateQueries({ queryKey: getListTasksQueryKey() });

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      await createTask.mutateAsync({
        data: {
          title: newTitle.trim(),
          completed: false,
          priority: newPriority,
          dueDate: newDueDate ?? undefined,
        },
      });
      invalidateTasks();
      setNewTitle("");
      setNewPriority("medium");
      setNewDueDate(null);
    } catch (err) {
      toast({
        title: "Couldn't add the task",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const toggleTask = async (task: any) => {
    try {
      await updateTask.mutateAsync({ taskId: task.id, data: { ...task, completed: !task.completed } });
      invalidateTasks();
    } catch (err) {
      toast({
        title: "Couldn't update the task",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const updatePriority = async (task: any, priority: Priority) => {
    try {
      await updateTask.mutateAsync({ taskId: task.id, data: { ...task, priority } });
      invalidateTasks();
    } catch (err) {
      toast({
        title: "Couldn't change the priority",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const updateDueDate = async (task: any, dueDate: string | null) => {
    try {
      await updateTask.mutateAsync({ taskId: task.id, data: { ...task, dueDate: dueDate ?? undefined } });
      invalidateTasks();
    } catch (err) {
      toast({
        title: "Couldn't update the due date",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const removeTask = async (taskId: number) => {
    try {
      await deleteTask.mutateAsync({ taskId });
      invalidateTasks();
    } catch (err) {
      toast({
        title: "Couldn't delete the task",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const clearCompleted = async () => {
    const done = tasks?.filter((t) => t.completed) ?? [];
    try {
      await Promise.all(done.map((t) => deleteTask.mutateAsync({ taskId: t.id })));
      invalidateTasks();
    } catch (err) {
      toast({
        title: "Couldn't clear completed tasks",
        description: getApiErrorMessage(err, "Some tasks may not have been removed. Please try again."),
        variant: "destructive",
      });
    }
  };

  const { active, completed } = useMemo(() => {
    const all = tasks ?? [];
    const active = all
      .filter((t) => !t.completed)
      .sort((a, b) => {
        if (sortKey === "priority") {
          return (PRIORITY_ORDER[a.priority as Priority] ?? 1) - (PRIORITY_ORDER[b.priority as Priority] ?? 1);
        }
        if (sortKey === "dueDate") {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return a.dueDate < b.dueDate ? -1 : 1;
        }
        return new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime();
      });
    const completed = all.filter((t) => t.completed);
    return { active, completed };
  }, [tasks, sortKey]);

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My To Do</h1>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {([
            ["priority", "Priority"],
            ["dueDate", "Due Date"],
            ["createdAt", "Date Added"],
          ] as [SortKey, string][]).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSortKey(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                sortKey === key ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <ArrowUpDown className="h-3 w-3" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Task list */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-4">
        {isLoading && (
          <div className="p-6 space-y-3">
            {[...Array(4)].map((_, i) => <div key={i} className="h-10 bg-gray-100 animate-pulse rounded-lg" />)}
          </div>
        )}

        {!isLoading && active.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">
            Nothing to do — add a task below!
          </div>
        )}

        {active.map((task, i) => (
          <TaskRow
            key={task.id}
            task={task}
            isLast={i === active.length - 1}
            onToggle={() => toggleTask(task)}
            onPriority={(p) => updatePriority(task, p)}
            onDueDate={(d) => updateDueDate(task, d)}
            onDelete={() => removeTask(task.id)}
          />
        ))}

        {/* Completed section */}
        {completed.length > 0 && (
          <>
            <button
              onClick={() => setShowCompleted((v) => !v)}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-t text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <CheckCircle2 className="h-3.5 w-3.5 text-gray-400" />
              {completed.length} completed
              <span className="ml-auto">{showCompleted ? "Hide" : "Show"}</span>
            </button>
            {showCompleted && completed.map((task, i) => (
              <TaskRow
                key={task.id}
                task={task}
                isLast={i === completed.length - 1}
                onToggle={() => toggleTask(task)}
                onPriority={(p) => updatePriority(task, p)}
                onDueDate={(d) => updateDueDate(task, d)}
                onDelete={() => removeTask(task.id)}
              />
            ))}
            {showCompleted && (
              <div className="border-t px-4 py-2.5 flex justify-end bg-gray-50">
                <button onClick={clearCompleted} className="text-xs text-red-500 hover:text-red-700 transition-colors">
                  Clear completed
                </button>
              </div>
            )}
          </>
        )}

        {/* Add form */}
        <div className="border-t bg-gray-50 p-3">
          <form onSubmit={handleAdd}>
            <div className="flex items-center gap-2 mb-2">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Make it happen"
                className="flex-1 bg-white"
              />
              <Button type="submit" size="sm" disabled={createTask.isPending || !newTitle.trim()} data-write-action>
                Add
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <PriorityPicker value={newPriority} onChange={setNewPriority} />
              <Popover open={newDueDateOpen} onOpenChange={setNewDueDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`flex items-center gap-1 text-xs rounded-md px-2 py-1 transition-colors ${
                      newDueDate
                        ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <CalendarDays className="h-3 w-3" />
                    {newDueDate ? format(parseISO(newDueDate), "d MMM yyyy") : "Set due date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newDueDate ? parseISO(newDueDate) : undefined}
                    onSelect={(d) => { setNewDueDate(d ? format(d, "yyyy-MM-dd") : null); setNewDueDateOpen(false); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {newDueDate && (
                <button type="button" onClick={() => setNewDueDate(null)} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TaskRow({
  task,
  isLast,
  onToggle,
  onPriority,
  onDueDate,
  onDelete,
}: {
  task: any;
  isLast: boolean;
  onToggle: () => void;
  onPriority: (p: Priority) => void;
  onDueDate: (d: string | null) => void;
  onDelete: () => void;
}) {
  const priority = (task.priority ?? "medium") as Priority;
  const dateObj = task.dueDate ? parseISO(task.dueDate) : undefined;
  const overdue = dateObj && isPast(dateObj) && !isToday(dateObj) && !task.completed;
  const dueToday = dateObj && isToday(dateObj) && !task.completed;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 group transition-colors ${isLast ? "" : "border-b"}`}>
      {/* Checkbox */}
      <button onClick={onToggle} className="flex-shrink-0 focus:outline-none">
        {task.completed
          ? <CheckCircle2 className="h-5 w-5 text-pink-400" />
          : <Circle className="h-5 w-5 text-gray-300 group-hover:text-gray-400 transition-colors" />
        }
      </button>

      {/* Priority dot */}
      <span className={`flex-shrink-0 w-2 h-2 rounded-full ${PRIORITY_CONFIG[priority].dot} ${task.completed ? "opacity-30" : ""}`} />

      {/* Title */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${task.completed ? "text-gray-400 line-through" : "text-gray-900 font-medium"}`}>
          {task.title}
        </p>
        {task.dueDate && (
          <p className={`text-xs flex items-center gap-1 mt-0.5 ${overdue ? "text-red-500" : dueToday ? "text-blue-600 font-medium" : "text-gray-400"}`}>
            <CalendarDays className="h-3 w-3" />
            {overdue ? "Overdue · " : dueToday ? "Due today · " : ""}
            {format(parseISO(task.dueDate), "d MMM yyyy")}
          </p>
        )}
      </div>

      {/* Hover actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <PriorityPicker value={priority} onChange={onPriority} />
        <DueDateButton
          value={task.dueDate}
          onChange={onDueDate}
          onClear={() => onDueDate(null)}
        />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-gray-400 hover:text-red-600"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Always-visible badge when has due date but no hover */}
      {!task.completed && task.dueDate && (
        <Badge
          variant="outline"
          className={`flex-shrink-0 text-xs group-hover:hidden ${
            overdue ? "border-red-200 text-red-600 bg-red-50" : dueToday ? "border-blue-200 text-blue-600 bg-blue-50" : "border-gray-200 text-gray-500"
          }`}
        >
          {format(parseISO(task.dueDate), "d MMM")}
        </Badge>
      )}
    </div>
  );
}
